import { useCallback, useEffect, useRef } from "react"

import { useDebounce } from "../../hooks/useDebounce"
import { placeTooltip } from "../utils"

/**
 * @typedef {Object} TooltipData return
 * @property {MutableRefObject} containerRef the ref for the contianer
 * @property {MutableRefObject} tooltipRef the ref for the tool tip
 *
 */

/**
 * Main tooltip hook, takes care of listeners, positioning and theming
 *
 * @param {string} placement the placement or the preferrable placement if auto fit
 * @param {boolean} autofit whether or not the tooltip should autofit
 * @param {boolean} show if not undefined will influence the listeners since it is a controlled tooltip
 * @param {number} debounceTimeout the time to debounce on ms
 *
 * @returns {TooltipData} refs and themes for the tooltip
 */
export function useTooltip(placement, autoFit, show, debounceTimeout) {
  const containerRef = useRef()
  const tooltipRef = useRef()
  const visibilityRef = useRef()
  const mountRef = useRef()

  const positionTooltip = useCallback(() => {
    window.requestAnimationFrame(() => placeTooltip(placement, containerRef.current, tooltipRef.current, autoFit))
  }, [placement, autoFit])

  const _resizeListener = () => {
    const tooltipElement = tooltipRef.current
    if (tooltipElement) {
      tooltipElement.classList.remove("being-positioned")
    }
    positionTooltip()
  }
  const hideTooltip = () => {
    const tooltipElement = tooltipRef.current
    if (tooltipElement) {
      tooltipElement.classList.add("being-positioned")
    }
  }
  const resizeListener = useDebounce(debounceTimeout, _resizeListener, hideTooltip)

  const _moveListener = () => {
    if (show === false) {
      return
    }
    const anchorElement = containerRef.current
    const tooltipElement = tooltipRef.current
    if (!anchorElement || !tooltipElement) {
      return
    }
    let isHover = anchorElement.matches(":hover") || tooltipElement.matches(":hover")
    if (isHover) {
      visibilityRef.current = true
      positionTooltip()
      tooltipElement.classList.add("is-shown")
      return
    }
    if (visibilityRef.current) {
      visibilityRef.current = false
      tooltipElement.classList.remove("is-shown")
    }
  }
  const moveListener = useDebounce(debounceTimeout, _moveListener)

  useEffect(() => {
    mountRef.current = true
    window.addEventListener("resize", resizeListener)
    if (show) {
      positionTooltip()
    } else if (show !== false) {
      window.addEventListener("mousemove", moveListener)
    }
    const tooltip = tooltipRef.current
    return () => {
      window.removeEventListener("resize", resizeListener)
      if (!show) {
        window.removeEventListener("mousemove", moveListener)
      }
      if (!tooltip) {
        return
      }
      tooltip.parentNode.removeChild(tooltip)
    }
  }, [resizeListener, moveListener, show, positionTooltip])

  useEffect(() => {
    if (!mountRef.current) {
      return
    }
    positionTooltip()
  })
  return { containerRef, tooltipRef }
}
